// Quality Section - Clean Design with Flip Cards
import { useState, useEffect, useRef } from 'preact/hooks'
import * as echarts from 'echarts'
import { formatValue as formatValueUtil } from '../../../utils/formatUtils.js'
import { QUALITY, renderDefinition } from '../../../data/definitions.js'
import LockedMetricToken from '../../shared/LockedMetricToken.jsx'
import { isGated, countTieredMetrics } from '../../../utils/metricTiering.js'
import { openMetricUpsell, isDemoCatalogUnlocked } from '../../../state/demoLimitStore.js'
import { recordMetricLocked } from '../../../services/demoLimitService.js'
import { trackEvent } from '../../../modules/dataService.js'

export default function QualitySection({ cellData }) {
  const [flippedCards, setFlippedCards] = useState({})
  const capPlotRef = useRef(null)
  const eisPlotRef = useRef(null)
  const capChartInstance = useRef(null)
  const eisChartInstance = useRef(null)
  
  // Extract data from API (normalized data has flat structure)
  const batch = cellData?.batch || {}
  const aging = cellData?.aging || {}

  // DEMO metric-tiering: exact spread/loss withheld → coarse teaser band.
  const capSpreadBand = batch.CAP_spread_band || null
  const capLossBand = aging.CAP_loss_band || null

  // Render the "Relative" quality value: exact when present, else a teaser band
  // chip, else a PRO lock (if gated), else a genuine "—". Never a bare "—" for a
  // gated/teaser field — that reads as "Batemo has no data".
  const renderRelativeQuality = (relValue, band, shapeKey, signed = false) => {
    if (relValue != null) {
      const pct = (relValue * 100).toFixed(2)
      return (
        <>
          <span className="value-num">{signed && relValue > 0 ? `+${pct}` : pct}</span>
          <span className="value-unit"> %</span>
        </>
      )
    }
    if (band) {
      return <LockedMetricToken band={band} category="quality" label="Relative" />
    }
    if (isGated(cellData, shapeKey)) {
      return <LockedMetricToken gated unit="%" category="quality" />
    }
    return (
      <>
        <span className="value-num">—</span>
      </>
    )
  }
  
  // Batch data
  const capSpreadMean = batch.CAP_spread_mean || 0
  const capSpreadMin = batch.CAP_spread_min || 0
  const capSpreadMax = batch.CAP_spread_max || 0
  const capSpreadStdAbs = batch.CAP_spread_std_abs || 0
  const capSpreadStdRel = batch.CAP_spread_std_rel || 0
  const capSpreadArray = batch.CAP_spread || []
  
  // EIS data - already in mΩ from dataService (ohmToMilliOhm conversion)
  const eisSpreadMean = batch.EIS_spread_mean || 0
  const eisSpreadArray = batch.EIS_spread || []
  const eisSpreadMin = eisSpreadArray.length > 0 ? Math.min(...eisSpreadArray) : 0
  const eisSpreadMax = eisSpreadArray.length > 0 ? Math.max(...eisSpreadArray) : 0
  const eisSpreadStdAbs = batch.EIS_spread_std_abs || 0
  const eisSpreadStdRel = batch.EIS_spread_std_rel || 0
  
  // Aging data - CAP_loss_rel can be negative (capacity gain) or positive (capacity loss)
  const capLoss = aging.CAP_loss || 0
  const capLossRel = aging.CAP_loss_rel ?? 0  // Use nullish coalescing to preserve 0 and negative values
  // EIS increase - already in mΩ from dataService
  const eisIncrease = aging.EIS_increase || 0
  const eisIncreaseRel = aging.EIS_increase_rel || 0
  
  // Calculate quality indicators
  const batchQuality = capSpreadStdRel > 0 ? 
    Math.max(0, Math.min(100, (1 - capSpreadStdRel * 100) * 100)) : 100
  const agingHealth = capLossRel ? 
    Math.max(0, Math.min(100, (1 + capLossRel) * 100)) : 100
  
  // Helper functions
  // Format value with separate number and unit for styling
  const formatValueSplit = (value, unit = '', property = null) => {
    if (value == null || isNaN(value)) return { number: 'N/A', unit: '' }
    const formatted = formatValueUtil(value, unit, property)
    return { 
      number: formatted, 
      unit: unit 
    }
  }
  
  // Toggle flip state
  const toggleFlip = (id) => {
    setFlippedCards(prev => ({ ...prev, [id]: !prev[id] }))
  }
  
  // Render Capacity Spread Plot with ECharts
  useEffect(() => {
    if (!capPlotRef.current || capSpreadArray.length === 0) return
    
    // Initialize chart
    if (!capChartInstance.current) {
      capChartInstance.current = echarts.init(capPlotRef.current)
    }
    
    // Prepare data - cell numbers and values
    const cellNumbers = capSpreadArray.map((_, i) => `Cell ${i + 1}`)
    
    // Calculate dynamic y-axis range for better visibility of differences
    const minVal = Math.min(...capSpreadArray)
    const maxVal = Math.max(...capSpreadArray)
    const range = maxVal - minVal
    const yMin = minVal - range * 0.2
    const yMax = maxVal + range * 0.2
    
    const option = {
      backgroundColor: 'transparent',
      grid: { 
        left: 70, 
        right: 30, 
        top: 20, 
        bottom: 50 
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        data: cellNumbers,
        axisLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } 
        },
        axisLabel: { 
          color: '#999', 
          fontFamily: 'Roboto', 
          fontSize: 11,
          rotate: 45
        },
        splitLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } 
        }
      },
      yAxis: {
        type: 'value',
        name: 'Capacity / Ah',
        nameLocation: 'middle',
        nameGap: 50,
        min: yMin,
        max: yMax,
        nameTextStyle: { 
          color: '#cfd2d6', 
          fontSize: 12, 
          fontFamily: 'Roboto' 
        },
        axisLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } 
        },
        axisLabel: { 
          color: '#999', 
          fontFamily: 'Roboto', 
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } 
        }
      },
      series: [
        {
          type: 'scatter',
          name: 'Capacity',
          data: capSpreadArray,
          symbolSize: 12,
          itemStyle: { 
            color: '#0d7c99' 
          },
          markLine: {
            silent: true,
            symbol: 'none',
            label: {
              show: false
            },
            lineStyle: {
              color: '#ea5b0c',
              width: 2,
              type: 'dashed'
            },
            data: [
              {
                name: `Mean: ${formatValueUtil(capSpreadMean, 'Ah', 'CAP_spread_mean')} Ah`,
                yAxis: capSpreadMean
              },
              [
                {
                  name: 'Mean - Std',
                  yAxis: capSpreadMean - capSpreadStdAbs,
                  lineStyle: {
                    color: 'rgba(234, 91, 12, 0.4)',
                    width: 1,
                    type: 'solid'
                  }
                },
                {
                  name: 'Mean + Std',
                  yAxis: capSpreadMean + capSpreadStdAbs,
                  lineStyle: {
                    color: 'rgba(234, 91, 12, 0.4)',
                    width: 1,
                    type: 'solid'
                  }
                }
              ]
            ]
          }
        }
      ],
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#0d7c99',
        textStyle: { 
          color: '#fff', 
          fontFamily: 'Roboto', 
          fontSize: 12 
        },
        formatter: (params) => {
          const p = params[0]
          const deviationVal = p.value - capSpreadMean
          const deviation = formatValueUtil(Math.abs(deviationVal), 'Ah', 'CAP_deviation_abs')
          const sign = deviationVal >= 0 ? '+' : '-'
          return `${p.name}<br/>Capacity: ${formatValueUtil(p.value, 'Ah', 'CAP_spread')} Ah<br/>Deviation: ${sign}${deviation} Ah`
        }
      }
    }
    
    capChartInstance.current.setOption(option)
    
    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      capChartInstance.current?.resize()
    })
    resizeObserver.observe(capPlotRef.current)
    
    return () => {
      resizeObserver.disconnect()
      capChartInstance.current?.dispose()
      capChartInstance.current = null
    }
  }, [capSpreadArray, capSpreadMean, capSpreadMin, capSpreadMax])
  
  // Render EIS Spread Plot with ECharts
  useEffect(() => {
    if (!eisPlotRef.current || eisSpreadArray.length === 0) return
    
    // Initialize chart
    if (!eisChartInstance.current) {
      eisChartInstance.current = echarts.init(eisPlotRef.current)
    }
    
    // Prepare data - cell numbers and values
    const cellNumbers = eisSpreadArray.map((_, i) => `Cell ${i + 1}`)
    
    // Calculate dynamic y-axis range for better visibility of differences
    const minVal = Math.min(...eisSpreadArray)
    const maxVal = Math.max(...eisSpreadArray)
    const range = maxVal - minVal
    const yMin = minVal - range * 0.2
    const yMax = maxVal + range * 0.2
    
    const option = {
      backgroundColor: 'transparent',
      grid: { 
        left: 70, 
        right: 30, 
        top: 20, 
        bottom: 50 
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        data: cellNumbers,
        axisLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } 
        },
        axisLabel: { 
          color: '#999', 
          fontFamily: 'Roboto', 
          fontSize: 11,
          rotate: 45
        },
        splitLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } 
        }
      },
      yAxis: {
        type: 'value',
        name: 'Impedance / mΩ',
        nameLocation: 'middle',
        nameGap: 50,
        min: yMin,
        max: yMax,
        nameTextStyle: { 
          color: '#cfd2d6', 
          fontSize: 12, 
          fontFamily: 'Roboto' 
        },
        axisLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } 
        },
        axisLabel: { 
          color: '#999', 
          fontFamily: 'Roboto', 
          fontSize: 11,
          showMinLabel: false,
          showMaxLabel: false
        },
        splitLine: { 
          lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } 
        }
      },
      series: [
        {
          type: 'scatter',
          name: 'Impedance',
          data: eisSpreadArray,
          symbolSize: 12,
          itemStyle: { 
            color: '#0d7c99' 
          },
          markLine: {
            silent: true,
            symbol: 'none',
            label: {
              show: false
            },
            lineStyle: {
              color: '#ea5b0c',
              width: 2,
              type: 'dashed'
            },
            data: [
              {
                name: `Mean: ${formatValueUtil(eisSpreadMean, 'mΩ', 'EIS_spread_mean')} mΩ`,
                yAxis: eisSpreadMean
              },
              [
                {
                  name: 'Mean - Std',
                  yAxis: eisSpreadMean - eisSpreadStdAbs,
                  lineStyle: {
                    color: 'rgba(234, 91, 12, 0.4)',
                    width: 1,
                    type: 'solid'
                  }
                },
                {
                  name: 'Mean + Std',
                  yAxis: eisSpreadMean + eisSpreadStdAbs,
                  lineStyle: {
                    color: 'rgba(234, 91, 12, 0.4)',
                    width: 1,
                    type: 'solid'
                  }
                }
              ]
            ]
          }
        }
      ],
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#0d7c99',
        textStyle: { 
          color: '#fff', 
          fontFamily: 'Roboto', 
          fontSize: 12 
        },
        formatter: (params) => {
          const p = params[0]
          const deviationVal = p.value - eisSpreadMean
          const deviation = formatValueUtil(Math.abs(deviationVal), 'mΩ', 'EIS_deviation_abs')
          const sign = deviationVal >= 0 ? '+' : '-'
          return `${p.name}<br/>Impedance: ${formatValueUtil(p.value, 'mΩ', 'EIS_spread')} mΩ<br/>Deviation: ${sign}${deviation} mΩ`
        }
      }
    }
    
    eisChartInstance.current.setOption(option)
    
    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      eisChartInstance.current?.resize()
    })
    resizeObserver.observe(eisPlotRef.current)
    
    return () => {
      resizeObserver.disconnect()
      eisChartInstance.current?.dispose()
      eisChartInstance.current = null
    }
  }, [eisSpreadArray, eisSpreadMean])
  
  const tieredCount = countTieredMetrics(cellData)
  const showTieringCue = isDemoCatalogUnlocked() && tieredCount > 0

  return (
    <div className="quality-section-unified" data-tour="quality-section">
      {/* DEMO metric-tiering: aggregate PRO cue for the measured metrics gated
          in this section (exact batch spread / aging loss / impedance). */}
      {showTieringCue && (
        <button
          type="button"
          className="locked-metric-more"
          onClick={() => {
            recordMetricLocked('quality')
            trackEvent('Demo Metric Locked', { category: 'quality' })
            openMetricUpsell('quality')
          }}
          title="Unlock the exact measured values with PRO"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          +{tieredCount} more measured metrics with PRO
        </button>
      )}
      {/* Main Content - Side by Side Layout */}
      <div className="quality-content-sidebyside">
        
        {/* Group 1: Cell-to-Cell Variation - Flip Card */}
        <div className={`key-spec-card quality-batch-card flipcard ${flippedCards['batch'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('batch')} title="Show info">?</button>
              <h3 className="column-title">Cell-to-Cell Variation</h3>
              <p className="card-subtitle">Cell-to-cell consistency in capacity and impedance</p>
              
              <div className="batch-plots-grid">
                {/* Capacity Plot */}
                {capSpreadArray.length > 0 && (
                  <div className="batch-plot-container">
                    <h4 className="plot-subtitle">Capacity Spread</h4>
                    <div className="plot-container" ref={capPlotRef}></div>
                  </div>
                )}
                
                {/* Impedance Plot */}
                {eisSpreadArray.length > 0 && (
                  <div className="batch-plot-container">
                    <h4 className="plot-subtitle">Impedance Spread</h4>
                    <div className="plot-container" ref={eisPlotRef}></div>
                  </div>
                )}
              </div>
              
              <div className="batch-stats-grid">
                {/* Capacity Deviation */}
                <div className="batch-stat-block">
                  <h4 className="stat-block-title">CAPACITY DEVIATION</h4>
                  
                  <div className="stat-group">
                    <div className="stat-group-title">Standard Deviation</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(capSpreadStdAbs, 'Ah', 'CAP_spread_std_abs').number}</span>
                        <span className="value-unit"> Ah</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        {renderRelativeQuality(
                          batch.CAP_spread_std_rel != null ? batch.CAP_spread_std_rel : null,
                          capSpreadBand,
                          'batch.CAP_spread_std_rel'
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="stat-group">
                    <div className="stat-group-title">Range</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(capSpreadMin, '', 'CAP_spread_min').number} - {formatValueSplit(capSpreadMax, 'Ah', 'CAP_spread_max').number}</span>
                        <span className="value-unit"> Ah</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueUtil(Math.abs((capSpreadMin - capSpreadMean) / capSpreadMean) * 100, '%', 'CAP_range_min_rel')} - {formatValueUtil(Math.abs((capSpreadMax - capSpreadMean) / capSpreadMean) * 100, '%', 'CAP_range_max_rel')}</span>
                        <span className="value-unit"> %</span>
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Impedance Deviation */}
                <div className="batch-stat-block">
                  <h4 className="stat-block-title">IMPEDANCE DEVIATION</h4>
                  
                  <div className="stat-group">
                    <div className="stat-group-title">Standard Deviation</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(eisSpreadStdAbs, 'mΩ', 'EIS_spread_std_abs').number}</span>
                        <span className="value-unit"> mΩ</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        {batch.EIS_spread_std_rel != null ? (
                          <>
                            <span className="value-num">{formatValueUtil(eisSpreadStdRel * 100, '%', 'EIS_spread_std_rel')}</span>
                            <span className="value-unit"> %</span>
                          </>
                        ) : isGated(cellData, 'batch.EIS_spread_std_rel') ? (
                          <LockedMetricToken gated unit="%" category="quality" />
                        ) : (
                          <span className="value-num">—</span>
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div className="stat-group">
                    <div className="stat-group-title">Range</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(eisSpreadMin, '', 'EIS_spread_min').number} - {formatValueSplit(eisSpreadMax, 'mΩ', 'EIS_spread_max').number}</span>
                        <span className="value-unit"> mΩ</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueUtil(Math.abs((eisSpreadMin - eisSpreadMean) / eisSpreadMean) * 100, '%', 'EIS_range_min_rel')} - {formatValueUtil(Math.abs((eisSpreadMax - eisSpreadMean) / eisSpreadMean) * 100, '%', 'EIS_range_max_rel')}</span>
                        <span className="value-unit"> %</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('batch')} title="Close">×</button>
              <h3 className="column-title">{QUALITY.batchVariation.title}</h3>
              <div className="flip-content">
                <p>
                  {QUALITY.batchVariation.intro}
                </p>
                <p style="margin-top: 12px;">
                  <strong>{QUALITY.batchVariation.capacity.title}:</strong> {renderDefinition(QUALITY.batchVariation.capacity.text, cellData)}
                </p>
                <p style="margin-top: 12px;">
                  <strong>{QUALITY.batchVariation.impedance.title}:</strong> {QUALITY.batchVariation.impedance.text}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Group 2: Post-Formation Loss - Flip Card */}
        <div className={`key-spec-card quality-aging-card flipcard ${flippedCards['aging'] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            <div className="flipcard-front">
              <button className="flip-btn" onClick={() => toggleFlip('aging')} title="Show info">?</button>
              <h3 className="column-title">Post-Formation Loss</h3>
              <p className="card-subtitle">Capacity fade and impedance growth after initial cycles</p>
              
              <div className="aging-stats-grid">
                {/* POST-FORMATION LOSS Card */}
                <div className="aging-stat-block">
                  <h4 className="stat-block-title">POST-FORMATION LOSS</h4>
                  
                  <div className="stat-group">
                    <div className="stat-group-title">Capacity Fade</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(capLoss, 'Ah', 'CAP_loss_abs').number}</span>
                        <span className="value-unit"> Ah</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        {renderRelativeQuality(
                          aging.CAP_loss_rel != null ? aging.CAP_loss_rel : null,
                          capLossBand,
                          'aging.CAP_loss_rel'
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="stat-group">
                    <div className="stat-group-title">Impedance Growth</div>
                    <div className="spec-row">
                      <span className="spec-label">Absolute</span>
                      <span className="spec-value">
                        <span className="value-num">{formatValueSplit(eisIncrease, 'mΩ', 'EIS_increase_abs').number}</span>
                        <span className="value-unit"> mΩ</span>
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Relative</span>
                      <span className="spec-value">
                        {aging.EIS_increase_rel != null ? (
                          <>
                            <span className="value-num">{(eisIncreaseRel * 100).toFixed(2)}</span>
                            <span className="value-unit"> %</span>
                          </>
                        ) : isGated(cellData, 'aging.EIS_increase_rel') ? (
                          <LockedMetricToken gated unit="%" category="quality" />
                        ) : (
                          <span className="value-num">—</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('aging')} title="Close">×</button>
              <h3 className="column-title">{QUALITY.initialAging.title}</h3>
              <div className="flip-content">
                <p>
                  {renderDefinition(QUALITY.initialAging.intro, cellData)}
                </p>
                <p style="margin-top: 12px;">
                  <strong>{QUALITY.initialAging.capacityFade.title}:</strong> {QUALITY.initialAging.capacityFade.text}
                </p>
                <p style="margin-top: 12px;">
                  <strong>{QUALITY.initialAging.impedanceGrowth.title}:</strong> {QUALITY.initialAging.impedanceGrowth.text}
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
